import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Person as ManIcon,
  Build as MachineIcon,
  Category as MaterialIcon,
  Route as MethodIcon,
  Straighten as MeasurementIcon,
  CheckCircleOutline as NoIssueIcon,
  CheckCircle as CheckIcon,
  Link as LinkIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import qualityManagementService from '../services/qualityManagement.service';

// ── Types & Constants ────────────────────────────────────────────────────────

type SixMCategory = 'man' | 'machine' | 'material' | 'method' | 'measurement';

export interface SixMEntry {
  category: SixMCategory;
  links?: string[];
  submitted_at?: string;
  submitted_by?: string;
  [key: string]: any;
}

interface ReviewRef {
  id: number;
  batch_number: string;
  trello_card_name: string;
  machine_type: string;
  issue_type: string | null;
  rejection_type?: string;
  sixm_entries?: SixMEntry[] | null;
  external_issues?: string[] | null;
  description?: string | null;
  action_plan?: string | null;
  no_issue?: number;
  no_issue_remarks?: string | null;
  status?: string;
}

interface Props {
  open: boolean;
  review: ReviewRef | null;
  onClose: () => void;
  onSubmitted: () => void;
}

const MACHINES = ['R1', 'R4', 'R5', 'MS', 'Sublimation', '48-Digital', 'Steamer 1', 'Steamer 2', 'Washer'];
const SHIFTS   = ['Day', 'Night'];

const CATEGORIES: { value: SixMCategory; label: string; color: string; bg: string; icon: React.ReactNode }[] = [
  { value: 'man',         label: 'Man',         color: '#1565C0', bg: '#E3F2FD', icon: <ManIcon />         },
  { value: 'machine',     label: 'Machine',     color: '#E65100', bg: '#FBE9E7', icon: <MachineIcon />     },
  { value: 'material',    label: 'Material',    color: '#2E7D32', bg: '#E8F5E9', icon: <MaterialIcon />    },
  { value: 'method',      label: 'Method',      color: '#6A1B9A', bg: '#F3E5F5', icon: <MethodIcon />      },
  { value: 'measurement', label: 'Measurement', color: '#BF360C', bg: '#FBE9E7', icon: <MeasurementIcon /> },
];

// ── Field helpers ─────────────────────────────────────────────────────────────

const TF = ({
  label, field, data, set, multiline = false, rows = 2, type = 'text',
  placeholder = '', helperText = '', required = false,
}: {
  label: string; field: string; data: Record<string, any>;
  set: (f: string, v: any) => void;
  multiline?: boolean; rows?: number; type?: string;
  placeholder?: string; helperText?: string; required?: boolean;
}) => (
  <TextField
    fullWidth
    label={required ? label + ' *' : label}
    type={type}
    multiline={multiline}
    rows={multiline ? rows : undefined}
    value={data[field] || ''}
    onChange={(e) => set(field, e.target.value)}
    placeholder={placeholder}
    helperText={helperText}
    InputLabelProps={type === 'date' ? { shrink: true } : undefined}
    size="small"
  />
);

const SF = ({
  label, field, data, set, options, required = false,
}: {
  label: string; field: string; data: Record<string, any>;
  set: (f: string, v: any) => void; options: string[]; required?: boolean;
}) => (
  <TextField
    fullWidth
    select
    label={required ? label + ' *' : label}
    value={data[field] || ''}
    onChange={(e) => set(field, e.target.value)}
    size="small"
  >
    <MenuItem value=""><em>— select —</em></MenuItem>
    {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
  </TextField>
);

// ── Category-specific field sets ──────────────────────────────────────────────

const ManFields: React.FC<{ data: Record<string, any>; set: (f: string, v: any) => void }> = ({ data, set }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <TF label="Employee Number/s" field="employee_numbers" data={data} set={set}
        placeholder="E001, E002" helperText="Comma separated" />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TF label="Name/s" field="employee_names" data={data} set={set}
        placeholder="John, Jane" helperText="Comma separated" />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TF label="Date" field="date" data={data} set={set} type="date" />
    </Grid>
    <Grid item xs={12} sm={6}>
      <SF label="Shift" field="shift" data={data} set={set} options={SHIFTS} />
    </Grid>
    <Grid item xs={12}>
      <TF label="Description" field="description" data={data} set={set} multiline rows={3}
        placeholder="Describe the issue related to the person involved..." />
    </Grid>
    <Grid item xs={12}>
      <TF label="Action Plan" field="action_plan" data={data} set={set} multiline rows={3}
        placeholder="What action will be taken..." />
    </Grid>
  </Grid>
);

const MachineFields: React.FC<{ data: Record<string, any>; set: (f: string, v: any) => void }> = ({ data, set }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <SF label="Machine" field="machine" data={data} set={set} options={MACHINES} required />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TF label="Date" field="date" data={data} set={set} type="date" />
    </Grid>
    <Grid item xs={12} sm={6}>
      <SF label="Shift" field="shift" data={data} set={set} options={SHIFTS} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TF label="Employee Numbers" field="employee_numbers" data={data} set={set}
        placeholder="E001, E002" helperText="Comma separated" />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TF label="Colorist" field="colorist" data={data} set={set} />
    </Grid>
    <Grid item xs={12}>
      <TF label="Description" field="description" data={data} set={set} multiline rows={2}
        placeholder="Describe the machine-related issue..." />
    </Grid>
    <Grid item xs={12}>
      <TF label="Action Plan" field="action_plan" data={data} set={set} multiline rows={2} />
    </Grid>
    <Grid item xs={12}>
      <TF label="Preventive Action" field="preventive_action" data={data} set={set} multiline rows={2}
        placeholder="Steps to prevent recurrence..." />
    </Grid>
    <Grid item xs={12}>
      <TF label="Corrective Action" field="corrective_action" data={data} set={set} multiline rows={2}
        placeholder="Immediate corrective steps taken..." />
    </Grid>
  </Grid>
);

const MaterialFields: React.FC<{ data: Record<string, any>; set: (f: string, v: any) => void }> = ({ data, set }) => (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <TF label="Relevant Materials" field="materials" data={data} set={set} multiline rows={2}
        placeholder="List the materials involved in the rejection..." />
    </Grid>
    <Grid item xs={12}>
      <TF label="Description" field="description" data={data} set={set} multiline rows={2} />
    </Grid>
    <Grid item xs={12}>
      <TF label="Preventive Action" field="preventive_action" data={data} set={set} multiline rows={2} />
    </Grid>
    <Grid item xs={12}>
      <TF label="Corrective Action" field="corrective_action" data={data} set={set} multiline rows={2} />
    </Grid>
    <Grid item xs={12}>
      <TF label="Action Plan" field="action_plan" data={data} set={set} multiline rows={2} />
    </Grid>
  </Grid>
);

const MethodFields: React.FC<{ data: Record<string, any>; set: (f: string, v: any) => void }> = ({ data, set }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <TF label="Date" field="date" data={data} set={set} type="date" />
    </Grid>
    <Grid item xs={12} sm={6}>
      <SF label="Shift" field="shift" data={data} set={set} options={SHIFTS} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <SF label="Machine" field="machine" data={data} set={set} options={MACHINES} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TF label="Route" field="route" data={data} set={set}
        placeholder="e.g. Printing → Steamer → Washer" />
    </Grid>
    <Grid item xs={12}>
      <TF label="Description" field="description" data={data} set={set} multiline rows={2}
        placeholder="Describe the method-related issue..." />
    </Grid>
    <Grid item xs={12}>
      <TF label="Preventive Action" field="preventive_action" data={data} set={set} multiline rows={2} />
    </Grid>
    <Grid item xs={12}>
      <TF label="Corrective Action" field="corrective_action" data={data} set={set} multiline rows={2} />
    </Grid>
    <Grid item xs={12}>
      <TF label="Action Plan" field="action_plan" data={data} set={set} multiline rows={2} />
    </Grid>
  </Grid>
);

const MeasurementFields: React.FC<{ data: Record<string, any>; set: (f: string, v: any) => void }> = ({ data, set }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <TF label="Date" field="date" data={data} set={set} type="date" />
    </Grid>
    <Grid item xs={12}>
      <TF label="Description" field="description" data={data} set={set} multiline rows={3}
        placeholder="Describe the measurement-related issue..." />
    </Grid>
    <Grid item xs={12}>
      <TF label="Action Plan" field="action_plan" data={data} set={set} multiline rows={3} />
    </Grid>
  </Grid>
);

const FIELDS_MAP: Record<SixMCategory, React.FC<{ data: Record<string, any>; set: (f: string, v: any) => void }>> = {
  man:         ManFields,
  machine:     MachineFields,
  material:    MaterialFields,
  method:      MethodFields,
  measurement: MeasurementFields,
};

// ── Links Section ─────────────────────────────────────────────────────────────

const LinksSection: React.FC<{
  links: string[];
  newLink: string;
  setNewLink: (v: string) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}> = ({ links, newLink, setNewLink, onAdd, onRemove }) => (
  <Box mt={2}>
    <Box display="flex" alignItems="center" gap={1} mb={1}>
      <LinkIcon fontSize="small" sx={{ color: '#00695C' }} />
      <Typography variant="subtitle2" fontWeight={700}>Evidence Links</Typography>
      <Typography variant="caption" color="text.secondary">(optional — URLs for related files or evidence)</Typography>
    </Box>
    <Box display="flex" gap={1} mb={1}>
      <TextField
        fullWidth
        size="small"
        placeholder="https://drive.google.com/... or any URL"
        value={newLink}
        onChange={(e) => setNewLink(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
      />
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={onAdd}
        disabled={!newLink.trim()}
        sx={{ whiteSpace: 'nowrap', borderColor: '#00695C', color: '#00695C' }}
      >
        Add
      </Button>
    </Box>
    {links.length > 0 && (
      <Paper variant="outlined" sx={{ maxHeight: 140, overflow: 'auto' }}>
        <List dense disablePadding>
          {links.map((url, i) => (
            <ListItem key={i} divider={i < links.length - 1}>
              <ListItemText
                primary={
                  <a href={url} target="_blank" rel="noopener noreferrer"
                    style={{ color: '#1565C0', fontSize: '0.82rem', wordBreak: 'break-all' }}>
                    {url}
                  </a>
                }
              />
              <ListItemSecondaryAction>
                <IconButton size="small" edge="end" onClick={() => onRemove(i)}>
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>
    )}
  </Box>
);

// ── Submitted entries summary ─────────────────────────────────────────────────

const SubmittedEntries: React.FC<{
  entries: SixMEntry[];
  onEdit: (cat: SixMCategory) => void;
}> = ({ entries, onEdit }) => {
  if (entries.length === 0) return null;
  return (
    <Box mb={2}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Submitted Categories
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
        {entries.map((e) => {
          const cat = CATEGORIES.find((c) => c.value === e.category);
          return (
            <Chip
              key={e.category}
              icon={<CheckIcon style={{ color: cat?.color }} />}
              label={cat?.label || e.category}
              size="small"
              variant="outlined"
              sx={{ borderColor: cat?.color, color: cat?.color, fontWeight: 600 }}
              deleteIcon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
              onDelete={() => onEdit(e.category)}
            />
          );
        })}
      </Box>
    </Box>
  );
};

// ── Main Dialog ───────────────────────────────────────────────────────────────

const SixMAnalysisDialog: React.FC<Props> = ({ open, review, onClose, onSubmitted }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [entries,    setEntries]    = useState<SixMEntry[]>([]);
  const [mode,       setMode]       = useState<'6m' | 'no_issue'>('6m');
  const [category,   setCategory]   = useState<SixMCategory | null>(null);
  const [data,       setData]       = useState<Record<string, any>>({});
  const [links,      setLinks]      = useState<string[]>([]);
  const [newLink,    setNewLink]    = useState('');
  const [remarks,    setRemarks]    = useState('');
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open && review) {
      const loaded: SixMEntry[] = Array.isArray((review as any).sixm_entries)
        ? (review as any).sixm_entries : [];
      setEntries(loaded);
      setMode('6m');
      setCategory(null);
      setData({});
      setLinks([]);
      setNewLink('');
      setRemarks('');
      setError(null);
      setSuccessMsg(null);
    }
  }, [open, review]);

  const set = (field: string, value: any) => setData((prev) => ({ ...prev, [field]: value }));

  const handleSelectCategory = (cat: SixMCategory) => {
    setError(null);
    setSuccessMsg(null);
    setCategory(cat);
    const existing = entries.find((e) => e.category === cat);
    if (existing) {
      const { category: _c, links: l, submitted_at: _a, submitted_by: _b, ...rest } = existing;
      setData(rest);
      setLinks(Array.isArray(l) ? l : []);
    } else {
      setData({});
      setLinks([]);
    }
    setNewLink('');
  };

  const handleAddLink = () => {
    const url = newLink.trim();
    if (!url) return;
    setLinks((prev) => [...prev, url]);
    setNewLink('');
  };

  const handleRemoveLink = (i: number) => {
    setLinks((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handleClose = () => {
    setMode('6m');
    setCategory(null);
    setData({});
    setLinks([]);
    setNewLink('');
    setRemarks('');
    setError(null);
    setSuccessMsg(null);
    onClose();
  };

  const handleSubmit6M = async () => {
    if (!review || !category) return;
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const result = await qualityManagementService.submit6M(review.id, { category, links, ...data });
      const serverEntries: SixMEntry[] = Array.isArray(result.review?.sixm_entries)
        ? result.review.sixm_entries : [];
      if (serverEntries.length > 0) {
        setEntries(serverEntries);
      } else {
        const newEntry: SixMEntry = { category, links: [...links], ...data, submitted_at: new Date().toISOString() };
        setEntries((prev) => {
          const merged = [...prev];
          const idx = merged.findIndex((e) => e.category === category);
          if (idx >= 0) { merged[idx] = newEntry; } else { merged.push(newEntry); }
          return merged;
        });
      }
      const wasEditing = entries.some((e) => e.category === category);
      const catLabel   = CATEGORIES.find((c) => c.value === category)?.label || category;
      setSuccessMsg(wasEditing
        ? `${catLabel} 6M updated. You can edit other categories or close.`
        : `${catLabel} 6M added. You can add more categories or close.`);
      setCategory(null);
      setData({});
      setLinks([]);
      setNewLink('');
      onSubmitted();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit 6M');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitNoIssue = async () => {
    if (!review) return;
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await qualityManagementService.submitNoIssue(review.id, remarks);
      setSuccessMsg('Marked as no issue. Closing...');
      onSubmitted();
      setTimeout(handleClose, 1200);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit no-issue');
    } finally {
      setSaving(false);
    }
  };

  if (!review) return null;

  const activeCat   = CATEGORIES.find((c) => c.value === category);
  const FieldsComp  = category ? FIELDS_MAP[category] : null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2 } }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ bgcolor: '#004D40', color: '#fff', pb: 1.5 }}>
        <Typography variant="h6" fontWeight={700}>6M Analysis</Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
          {review.trello_card_name} &nbsp;·&nbsp; {review.batch_number}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
        {error      && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

        {review.description && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Submitter Remark
            </Typography>
            <Typography variant="body2">{review.description}</Typography>
          </Alert>
        )}

        {/* ── Mode toggle ── */}
        <Box display="flex" gap={1} mb={2}>
          <Button
            variant={mode === '6m' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => { setMode('6m'); setCategory(null); setError(null); setSuccessMsg(null); }}
            sx={{
              bgcolor: mode === '6m' ? '#004D40' : undefined,
              borderColor: '#004D40', color: mode === '6m' ? '#fff' : '#004D40',
              '&:hover': { bgcolor: mode === '6m' ? '#00695C' : '#E0F2F1' },
            }}
          >
            6M Analysis
          </Button>
          <Button
            variant={mode === 'no_issue' ? 'contained' : 'outlined'}
            size="small"
            startIcon={<NoIssueIcon />}
            onClick={() => { setMode('no_issue'); setCategory(null); setError(null); setSuccessMsg(null); }}
            sx={{
              bgcolor: mode === 'no_issue' ? '#1B5E20' : undefined,
              borderColor: '#1B5E20', color: mode === 'no_issue' ? '#fff' : '#1B5E20',
              '&:hover': { bgcolor: mode === 'no_issue' ? '#2E7D32' : '#E8F5E9' },
            }}
          >
            No Issue
          </Button>
        </Box>

        {/* ══════ 6M MODE ══════ */}
        {mode === '6m' && (
          <>
            {/* Summary of already-submitted categories */}
            <SubmittedEntries entries={entries} onEdit={handleSelectCategory} />

            {/* Category picker */}
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600} mb={1}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {entries.length === 0 ? 'Select a category to add' : 'Add or edit a category'}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {CATEGORIES.map((cat) => {
                const isSubmitted = entries.some((e) => e.category === cat.value);
                const isActive    = category === cat.value;
                return (
                  <Button
                    key={cat.value}
                    variant={isActive ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={isSubmitted
                      ? <CheckIcon style={{ color: isActive ? '#fff' : cat.color }} />
                      : cat.icon}
                    onClick={() => handleSelectCategory(cat.value)}
                    sx={{
                      borderColor: cat.color,
                      bgcolor: isActive ? cat.color : isSubmitted ? cat.bg : 'transparent',
                      color: isActive ? '#fff' : cat.color,
                      fontWeight: 600,
                      '&:hover': { bgcolor: cat.bg, borderColor: cat.color },
                    }}
                  >
                    {cat.label}
                    {isSubmitted && !isActive && (
                      <Chip label="✓" size="small" sx={{
                        ml: 0.5, height: 16, fontSize: '0.65rem',
                        bgcolor: cat.color, color: '#fff',
                      }} />
                    )}
                  </Button>
                );
              })}
            </Box>

            {/* Category form */}
            {category && activeCat && FieldsComp && (
              <Paper variant="outlined" sx={{ p: 2, borderColor: activeCat.color, borderWidth: 2, borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Box sx={{ color: activeCat.color, display: 'flex' }}>{activeCat.icon}</Box>
                  <Typography variant="subtitle1" fontWeight={700} color={activeCat.color}>
                    {activeCat.label}
                    {entries.some((e) => e.category === category) && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        (editing)
                      </Typography>
                    )}
                  </Typography>
                </Box>

                <FieldsComp data={data} set={set} />

                <Divider sx={{ my: 2 }} />

                {/* Evidence Links per category */}
                <LinksSection
                  links={links}
                  newLink={newLink}
                  setNewLink={setNewLink}
                  onAdd={handleAddLink}
                  onRemove={handleRemoveLink}
                />

                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="text"
                    onClick={() => { setCategory(null); setData({}); setLinks([]); setNewLink(''); }}
                    sx={{ mr: 1, color: 'text.secondary' }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit6M}
                    disabled={saving}
                    sx={{ bgcolor: activeCat.color, '&:hover': { filter: 'brightness(0.9)' } }}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {entries.some((e) => e.category === category) ? 'Update' : 'Save'} {activeCat.label}
                  </Button>
                </Box>
              </Paper>
            )}

            {!category && entries.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Select one or more categories above. A review can have multiple issues — e.g. both Man and Machine.
              </Typography>
            )}
          </>
        )}

        {/* ══════ NO ISSUE MODE ══════ */}
        {mode === 'no_issue' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Use this only if there is genuinely no issue identified. This will close the review without requiring approvals.
            </Alert>
            <TextField
              fullWidth multiline rows={4}
              label="Remarks (optional)"
              placeholder="Explain why there is no issue..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              size="small"
            />
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={saving} color="inherit">
          {entries.length > 0 ? 'Done' : 'Cancel'}
        </Button>
        {mode === 'no_issue' && (
          <Button
            variant="contained" color="success"
            onClick={handleSubmitNoIssue}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <NoIssueIcon />}
          >
            Confirm No Issue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SixMAnalysisDialog;
